import React, { useCallback, useEffect, useState } from "react";
import MUIDataTable from "mui-datatables";
import { Box, FormControl, InputLabel, MenuItem, Select, Switch, Typography } from "@mui/material";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import Axios from "../axios";

const ROWS_PER_PAGE = 10;

const CoachAssignment = () => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND;
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState({ name: "", direction: "asc" });
  const [loading, setLoading] = useState(false);

  const showError = (err) => {
    toast.error(err?.response?.data?.message || err?.response?.data?.error || "Something went wrong");
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(0);
      setSearch(searchInput);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const fetchRows = useCallback(() => {
    setLoading(true);
    Axios.get(`${BACKEND_URL}coach-eligibility/users`, {
      params: {
        page: page + 1,
        limit: ROWS_PER_PAGE,
        search: search || undefined,
        status: statusFilter,
        sortField: sortOrder.name || undefined,
        sortOrder: sortOrder.direction || undefined,
      },
    })
      .then((res) => {
        setRows(res?.data?.data?.docs || []);
        setTotalCount(res?.data?.data?.totalDocs || 0);
      })
      .catch(showError)
      .finally(() => setLoading(false));
  }, [BACKEND_URL, page, search, statusFilter, sortOrder]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const toggleEligibility = (userId, isEligible) => {
    setLoading(true);
    const request = isEligible
      ? Axios.patch(`${BACKEND_URL}coach-eligibility/${userId}/revoke`)
      : Axios.post(`${BACKEND_URL}coach-eligibility`, { userId });

    request
      .then((res) => {
        toast.success(res?.data?.message || (isEligible ? "Eligibility revoked" : "User assigned as eligible"));
        fetchRows();
      })
      .catch(showError)
      .finally(() => setLoading(false));
  };

  const columns = [
    {
      name: "name",
      label: "Name",
      options: {
        filter: false,
        customBodyRenderLite: (i) => rows[i]?.name || "-",
      },
    },
    {
      name: "mail",
      label: "Email",
      options: {
        filter: false,
        customBodyRenderLite: (i) => rows[i]?.mail || "-",
      },
    },
    {
      name: "assignedBy",
      label: "Assigned By",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (i) => rows[i]?.assignedBy || "-",
      },
    },
    {
      name: "actions",
      label: "Eligible",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (i) => (
          <Switch
            checked={Boolean(rows[i]?.isEligible)}
            disabled={loading}
            onChange={() => toggleEligibility(rows[i]?._id, Boolean(rows[i]?.isEligible))}
            size="small"
          />
        ),
      },
    },
  ];

  return (
    <div className="container-fluid">
      <Helmet>
        <title>Coach Assignment</title>
      </Helmet>
      <div className="d-flex justify-content-between align-center mb-3">
        <h2>Coach Assignment</h2>
      </div>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Search registered users and toggle "Eligible" to mark them eligible to be assigned as a coach/club/host head.
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="eligibility-filter-label">Eligibility</InputLabel>
          <Select
            labelId="eligibility-filter-label"
            label="Eligibility"
            value={statusFilter}
            onChange={(e) => {
              setPage(0);
              setStatusFilter(e.target.value);
            }}
          >
            <MenuItem value="all">All Users</MenuItem>
            <MenuItem value="eligible">Eligible Users</MenuItem>
            <MenuItem value="not_eligible">Non-Eligible Users</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <MUIDataTable
        title="Users"
        data={rows}
        columns={columns}
        options={{
          responsive: "standard",
          selectableRows: "none",
          filter: false,
          viewColumns: false,
          download: false,
          serverSide: true,
          search: true,
          searchText: searchInput,
          count: totalCount,
          page,
          sortOrder,
          rowsPerPage: ROWS_PER_PAGE,
          rowsPerPageOptions: [],
          textLabels: { body: { noMatch: loading ? "Loading..." : "No users found" } },
          onTableChange: (action, tableState) => {
            if (action === "changePage") {
              setPage(tableState.page);
            } else if (action === "sort") {
              setSortOrder(tableState.sortOrder);
              setPage(0);
            }
          },
          onSearchChange: (searchQuery) => {
            setSearchInput(searchQuery || "");
          },
          onSearchClose: () => {
            setSearchInput("");
          },
        }}
      />
    </div>
  );
};

export default CoachAssignment;
